import classnames from 'classnames/bind';
import { type ElementType, Fragment, type ReactNode } from 'react';
import { isNil } from 'lodash/fp';
import type { PolymorphicProps } from 'app/globalTypes';
import { useTheme } from 'app/components/core/ThemeProvider';
import { IconSize } from 'app/components/core/Icon';
import { FlexSpacer } from 'app/components/core/FlexSpacer';
import { HintIcon } from 'app/components/composite/HintIcon';
import * as styles from './FieldLabel.css';

const cx = classnames.bind(styles);

export interface Props {
    className?: string;
    testId?: string;
    tooltip?: ReactNode;
    help?: ReactNode;
    htmlFor?: string;
    children: ReactNode;
}

export function FieldLabel<As extends ElementType>(props: PolymorphicProps<As, Props>) {
    const { as: Component = 'label', className, testId, tooltip, help, children, ...rest } = props;
    const { themeClassname } = useTheme();
    const classes = cx('base', themeClassname, className);
    const helpIsString = typeof help === 'string';

    if (isNil(children)) {
        return null;
    }

    return (
        <Component data-testid={testId} className={classes} {...rest}>
            {children}
            {tooltip && (
                <HintIcon size={IconSize.MINI} placement="top" className={styles.icon}>
                    {tooltip}
                </HintIcon>
            )}
            {help && (
                <Fragment>
                    <FlexSpacer />
                    {helpIsString ? <span className={styles.helpColor}>{help}</span> : help}
                </Fragment>
            )}
        </Component>
    );
}
